import type { SerializedStyles } from '@emotion/react';
import { Box, Button, Flex } from '@inflearn/ds-react';
import { isNil } from 'lodash-es';
import { useEffect, useState } from 'react';
import { Controller, type SubmitHandler, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ApiError } from '../../../resources/ApiError';
import { logger } from '../../../resources/Logger';
import { useCaptcha } from '../../hooks/useCaptcha';
import useClientAuthPhoneVerificationCreateMutation from '../../mutations/useClientAuthPhoneVerificationCreateMutation';
import useClientAuthPhoneVerificationPublicCreateMutation from '../../mutations/useClientAuthPhoneVerificationPublicCreateMutation';
import { ERROR_MESSAGE } from '../../validators/constants';
import { PhoneInput } from './PhoneInput';
import ValidationMessage from './ValidationMessage';

interface PhoneVerificationFormType {
  phone: string;
  countryCode: string;
}

interface PhoneVerificationFormProps {
  onSend?: () => void;
  isSend: boolean;
  showPhoneInputLabel?: boolean;
  styleSubmitButton?: SerializedStyles;
  autoFocus?: boolean;
  existingPhoneNumber?: string;
  withCaptcha?: boolean;
}

export function PhoneVerificationForm({
  onSend,
  isSend,
  showPhoneInputLabel,
  styleSubmitButton,
  autoFocus = false,
  existingPhoneNumber,
  withCaptcha = false,
}: PhoneVerificationFormProps) {
  const formContext = useFormContext<PhoneVerificationFormType>();
  const phone = useWatch({ control: formContext.control, name: 'phone' });

  const [disabled, setDisabled] = useState(false);
  const [hasExceededMaxAttempts, setHasExceededMaxAttempts] = useState(false);

  const captcha = useCaptcha({ enabled: withCaptcha });
  const { t } = useTranslation();

  const handleError = (error: unknown) => {
    if (!ApiError.isApiError(error)) {
      logger.error(error);
      formContext.setError('phone', {
        type: 'manual',
        message: hasExceededMaxAttempts
          ? ERROR_MESSAGE.OVER_MAX_COUNT()
          : t('잠시 후 다시 시도해 주세요.'),
      });
      return;
    }

    const message = (() => {
      if (ApiError.isBadRequest(error.statusCode)) {
        return ERROR_MESSAGE.IS_NOT_KOREAN_NUMBER();
      }

      if (ApiError.isTooManyRequest(error.statusCode)) {
        setHasExceededMaxAttempts(true);
        return ERROR_MESSAGE.OVER_MAX_COUNT();
      }

      if (error.errorCode === 'INVALID_CAPTCHA_TOKEN') {
        return ERROR_MESSAGE.INVALID_CAPTCHA_TOKEN();
      }

      return ERROR_MESSAGE.IS_NOT_EMPTY();
    })();

    formContext.setError('phone', {
      type: 'manual',
      message,
    });
  };

  const mutationWithCaptcha = useClientAuthPhoneVerificationPublicCreateMutation({
    onSuccess: () => {
      captcha.resetCaptchaToken();
      onSend?.();
    },
    onError: (error) => {
      captcha.resetCaptchaToken();
      handleError(error);
    },
  });

  const mutationWithoutCaptcha = useClientAuthPhoneVerificationCreateMutation({
    onSuccess: onSend,
    onError: handleError,
  });

  const mutation = withCaptcha ? mutationWithCaptcha : mutationWithoutCaptcha;

  const handleSendRequest: SubmitHandler<PhoneVerificationFormType> = ({ countryCode, phone }) => {
    if (withCaptcha) {
      captcha.runWhenCaptchaExist((captchaToken) => {
        mutationWithCaptcha.mutate({
          countryCode,
          phone,
          captchaToken,
        });
      });
    } else {
      mutationWithoutCaptcha.mutate({
        countryCode,
        phone,
      });
    }
  };

  useEffect(() => {
    if (isNil(existingPhoneNumber)) {
      return;
    }

    setDisabled(existingPhoneNumber === phone);
  }, [phone]);

  return (
    <Flex justify="center" w="100%">
      {withCaptcha && <captcha.DevTools />}
      <Flex
        component="form"
        direction="column"
        w="100%"
        onSubmit={formContext.handleSubmit(handleSendRequest)}
      >
        <Controller
          control={formContext.control}
          name="phone"
          render={({ field: phoneField, fieldState }) => (
            <Controller
              control={formContext.control}
              name="countryCode"
              render={({ field: countryCodeField }) => (
                <PhoneInput
                  code={countryCodeField.value}
                  phoneNumber={phoneField.value}
                  onCodeChange={(value) => {
                    countryCodeField.onChange(value);
                    formContext.trigger('phone');
                  }}
                  onNumberChange={phoneField.onChange}
                  error={
                    fieldState.error?.message && (
                      <ValidationMessage message={fieldState.error.message} />
                    )
                  }
                  showLabel={showPhoneInputLabel}
                  autoFocus={autoFocus}
                >
                  <Button
                    variant="default"
                    size="md"
                    css={styleSubmitButton}
                    styles={{
                      label: {
                        visibility:
                          mutation.isLoading || (withCaptcha && captcha.isPending)
                            ? 'hidden'
                            : 'visible',
                      },
                    }}
                    miw={127}
                    fullWidth={withCaptcha}
                    type="submit"
                    disabled={disabled || mutation.isLoading}
                    loading={mutation.isLoading || (withCaptcha && captcha.isPending)}
                    loaderPosition="center"
                  >
                    {isSend ? t('재전송') : t('인증 요청')}
                  </Button>
                </PhoneInput>
              )}
            />
          )}
        />
        {withCaptcha && (
          <Box ref={captcha.captchaRef} className={captcha.captchaClassName} mx="auto" />
        )}
      </Flex>
    </Flex>
  );
}
